/* fitment-notes.js — Task 3 of the builder fitment-notes feature
 * (docs/superpowers/plans/2026-07-16-builder-fitment-notes.md).
 *
 * Client renderer for Task 2's snippets/fitment-notes.liquid blob
 * (script.cpb-fitment-notes-data: [{gens:[full GID...], text, style}]) +
 * mount(s) (.cpb-fitment-notes-mount, each starts [hidden]).
 *
 * CLASS not id (review fix, Task 5 follow-up): a merchant can add
 * sections/fitment-notes.liquid to a BUILDER product's template, putting
 * TWO copies of the snippet (blob + mount) on one page. Duplicate ids are
 * invalid HTML and getElementById() only ever returns the first, so this
 * reads the FIRST blob (content is identical per-product, so "first" is
 * fine) via querySelector, but renders into EVERY mount via
 * querySelectorAll — each mount gets its own independent accordion built
 * from the same matched entries. All mounts share the single
 * COLLAPSE_KEY sessionStorage entry (collapsing one collapses "the"
 * fitment-notes accordion concept sitewide) — acceptable and simplest;
 * on any render every mount reads the same key.
 *
 * GID-ONLY MATCHING (Ben, hard rule): the saved vehicle's generation is
 * matched by GID only (gidId() reduces any GID form to its trailing
 * numeric id, so a prefix difference can never affect the match) —
 * NEVER by handle or name. Mirrored verbatim (gidId/matchNotes/
 * splitByStyle) by test/fitment-notes.test.js — that test is the
 * contract; keep this copy character-identical to it.
 *
 * Standalone IIFE — no shared scope with the builder's own script
 * (custom-build-product-script-new.js), even though both read the same
 * vehicle_selection cookie shape (mirrored from that file's
 * getVehicleSelection()/gidId(), :59-67 — read, not guessed).
 *
 * Idempotent: Task 5 may load this file twice on the same page (or the
 * theme editor may re-run it on shopify:section:load) — window.__tsoFitmentNotes
 * guards double-binding of the document-level listeners; render() itself
 * always clears every mount first, so a re-render is always safe.
 */
(function () {
  'use strict';
  if (window.__tsoFitmentNotes) return;
  window.__tsoFitmentNotes = true;

  var COLLAPSE_KEY = 'tso_fn_collapsed';

  // ---- cookie read (mirrors custom-build-product-script-new.js:59-67) ----
  function getVehicleSelection() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)vehicle_selection=([^;]+)/);
      if (!m) return null;
      var v = JSON.parse(decodeURIComponent(m[1]));
      return (v && v.year && v.make && v.model) ? v : null;
    } catch (e) { return null; }
  }

  // ---- match/group logic (character-identical to test/fitment-notes.test.js) ----
  function gidId(v) { return String(v == null ? '' : v).split('/').pop(); }

  // A note with NO generations is UNIVERSAL: it shows on every vehicle, and
  // with no vehicle saved at all. Generations, when present, still restrict.
  function matchNotes(entries, genGid) {
    var key = genGid ? gidId(genGid) : null;
    return entries.filter(function (e) {
      var gens = e.gens || [];
      if (!gens.length) return true;              // universal note
      if (!key) return false;                     // restricted note, no vehicle => hide
      return gens.map(gidId).indexOf(key) !== -1; // GID vs GID, never text
    });
  }

  function splitByStyle(matched) {
    var urgent = [], info = [];
    matched.forEach(function (e) {
      (String(e.style).toLowerCase() === 'urgent' ? urgent : info).push(e);
    });
    return { urgent: urgent, info: info };
  }

  // ---- blob parse (once; first match only — content is identical per-product) ----
  function readEntries() {
    var el = document.querySelector('.cpb-fitment-notes-data');
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent || el.innerText || '[]');
      return Array.isArray(data) ? data : [];
    } catch (e) { return []; }
  }

  // static, theme-controlled markup only — never interpolates note text
  var WARNING_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M12 3.5 L22 20.5 L2 20.5 Z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/>' +
    '<line x1="12" y1="9.5" x2="12" y2="14.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>' +
    '<circle cx="12" cy="17.25" r="1" fill="currentColor"/>' +
    '</svg>';

  function buildUrgentCard(text) {
    var card = document.createElement('div');
    card.className = 'fn-card fn-card--urgent';
    var icon = document.createElement('span');
    icon.className = 'fn-card__icon';
    icon.innerHTML = WARNING_SVG; // static markup, no user data
    var body = document.createElement('span');
    body.className = 'fn-card__text';
    body.textContent = text;
    card.appendChild(icon);
    card.appendChild(body);
    return card;
  }

  function buildInfoAccordion(infoTexts) {
    var details = document.createElement('details');
    details.className = 'fn-acc tso-pdp__acc';
    details.open = sessionStorage.getItem(COLLAPSE_KEY) !== '1';

    var summary = document.createElement('summary');
    summary.className = 'fn-acc__sum tso-pdp__acc-sum';
    summary.textContent = 'Fitment Notes';
    details.appendChild(summary);

    infoTexts.forEach(function (text) {
      var row = document.createElement('div');
      row.className = 'fn-acc__row';
      row.textContent = text;
      details.appendChild(row);
    });

    details.addEventListener('toggle', function () {
      try { sessionStorage.setItem(COLLAPSE_KEY, details.open ? '0' : '1'); } catch (e) {}
    });

    return details;
  }

  // renders the already-matched/grouped entries into ONE mount — called
  // once per element returned by querySelectorAll so every instance on
  // the page (builder + a merchant-added section, worst case) gets its
  // own independent DOM/accordion built from the same data.
  function renderInto(mount, grouped) {
    mount.innerHTML = '';

    // On the PDP editor section the mount sits inside a padded
    // .fitment-notes-section wrapper; collapse the whole wrapper when there is
    // nothing to show so it doesn't leave an empty padded gap. The builder
    // mount has no such ancestor, so this is a no-op there.
    var section = mount.closest ? mount.closest('.fitment-notes-section') : null;

    if (!grouped) {
      mount.hidden = true;
      if (section) section.hidden = true;
      return;
    }

    grouped.urgent.forEach(function (e) {
      mount.appendChild(buildUrgentCard(e.text));
    });

    if (grouped.info.length) {
      mount.appendChild(buildInfoAccordion(grouped.info.map(function (e) { return e.text; })));
    }

    mount.hidden = false;
    if (section) section.hidden = false;
  }

  function render() {
    var mounts = document.querySelectorAll('.cpb-fitment-notes-mount');
    if (!mounts.length) return;

    var entries = readEntries();
    var grouped = null;
    if (entries.length) {
      var sel = getVehicleSelection();
      var genGid = sel ? sel.filter_value : null;
      var matched = matchNotes(entries, genGid);
      if (matched.length) grouped = splitByStyle(matched);
    }

    for (var i = 0; i < mounts.length; i++) {
      renderInto(mounts[i], grouped);
    }
  }

  function start() {
    render();
    document.addEventListener('vehicle:selection:updated', render);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
